import { useState } from "react";
import type { EducationLevel } from "../../types/education";

export type CourseDetails = {
  guardianName: string;
  email: string;
  phone: string;
  studentName: string;
  studentId?: string;
  enrolled: boolean;                 // MUST be true
  intake: "Term 1" | "Term 2" | "Term 3" | "Semester 1" | "Semester 2";
  startDate: string;                 // ISO
  annualTuitionAED: number;
  // Higher ed (optional):
  semester?: "Semester 1" | "Semester 2";
  creditHours?: number;
};

export default function CourseDetailsForm({
  defaultPriceAED,
  level,
  termCount,
  onSubmit
}: {
  defaultPriceAED: number;
  level: EducationLevel;
  termCount: 2 | 3;
  onSubmit: (d: CourseDetails) => void;
}) {
  const isHigher = level === "higher";

  const defaultIntake = isHigher ? "Semester 1" : (termCount === 3 ? "Term 1" : "Semester 1");

  const [form, setForm] = useState<CourseDetails>({
    guardianName: "",
    email: "",
    phone: "",
    studentName: "",
    studentId: "",
    enrolled: false,
    intake: defaultIntake as CourseDetails["intake"],
    startDate: new Date().toISOString().slice(0,10),
    annualTuitionAED: defaultPriceAED,
    semester: isHigher ? "Semester 1" : undefined,
    creditHours: isHigher ? 15 : undefined
  });

  const handle = (k: keyof CourseDetails, v: any) => setForm(prev => ({ ...prev, [k]: v }));

  const valid = form.guardianName && form.email && form.phone && form.studentName && form.enrolled;

  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <h2 className="text-xl font-semibold mb-4 text-foreground">Course details</h2>

      <div className="grid md:grid-cols-2 gap-4">
        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
          placeholder="Guardian full name"
          value={form.guardianName} 
          onChange={e=>handle("guardianName", e.target.value)} 
        />
        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
          placeholder="Email"
          type="email"
          value={form.email} 
          onChange={e=>handle("email", e.target.value)} 
        />
        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
          placeholder="Phone"
          type="tel"
          value={form.phone} 
          onChange={e=>handle("phone", e.target.value)} 
        />
        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
          placeholder="Student name"
          value={form.studentName} 
          onChange={e=>handle("studentName", e.target.value)} 
        />
        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
          placeholder="Student ID (optional)"
          value={form.studentId || ""} 
          onChange={e=>handle("studentId", e.target.value)} 
        />

        {/* Intake selector respects level/termCount */}
        <select 
          className="border border-border rounded-lg p-3 bg-background text-foreground" 
          value={form.intake} 
          onChange={e=>handle("intake", e.target.value as any)}
        >
          {isHigher ? (
            <>
              <option>Semester 1</option>
              <option>Semester 2</option>
            </>
          ) : termCount === 3 ? (
            <>
              <option>Term 1</option>
              <option>Term 2</option>
              <option>Term 3</option>
            </>
          ) : (
            <>
              <option>Semester 1</option>
              <option>Semester 2</option>
            </>
          )}
        </select>

        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground" 
          type="date"
          value={form.startDate} 
          onChange={e=>handle("startDate", e.target.value)} 
        />

        <input 
          className="border border-border rounded-lg p-3 bg-background text-foreground" 
          type="number" 
          min={0}
          value={form.annualTuitionAED}
          onChange={e=>handle("annualTuitionAED", Number(e.target.value))} 
        />

        {/* Higher Ed specific (optional inputs) */}
        {isHigher && (
          <>
            <select 
              className="border border-border rounded-lg p-3 bg-background text-foreground" 
              value={form.semester} 
              onChange={e=>handle("semester", e.target.value as any)}
            >
              <option>Semester 1</option>
              <option>Semester 2</option>
            </select>
            <input 
              className="border border-border rounded-lg p-3 bg-background text-foreground placeholder:text-muted-foreground" 
              type="number" 
              min={0}
              placeholder="Credit hours (optional)"
              value={form.creditHours || 0}
              onChange={e=>handle("creditHours", Number(e.target.value))} 
            />
          </>
        )}
      </div>

      <label className="flex items-center gap-2 mt-6 cursor-pointer">
        <input 
          type="checkbox" 
          checked={form.enrolled} 
          onChange={e=>handle("enrolled", e.target.checked)}
          className="w-4 h-4" 
        />
        <span className="text-sm text-foreground">I confirm the student is already enrolled at this school.</span>
      </label>

      <div className="text-xs text-muted-foreground mt-3 p-3 bg-muted rounded-lg">
        UAE rule of thumb: we finance school-issued invoices (tuition already invoiced). You may be asked for an enrolment letter or invoice during partner KYC.
      </div>

      <div className="mt-6">
        <button 
          disabled={!valid}
          className={`w-full px-4 py-3 rounded-lg font-medium transition-colors ${
            valid 
              ? "bg-primary text-primary-foreground hover:bg-primary/90" 
              : "bg-muted text-muted-foreground cursor-not-allowed"
          }`}
          onClick={()=>onSubmit(form)}
        >
          Continue
        </button>
      </div>
    </div>
  );
}